'use client';

import React, { useEffect, useMemo } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useLayoutControl } from 'src/context/layout';
import { useCRMButtonContext } from 'src/context/providers/PushToCRMButtonProvider';
import { usePushToCRMMutation, useGetMappingsQuery, useGetUserInputOptionsQuery } from 'src/api/redux';
import { entityTypeSlugMap } from 'src/components/Settings/constants';

import PushToCRMV2 from '../Buttons/PushToCRMV2';
import CRMMaxMenuV2 from './CRMMaxMenu/CRMMaxMenuV2';

// Case 1: No mappings - Button will not show
// Case 1a: One or More mapping, but mapping not enabled - button will not show
// Case 2: One mapping, no options - Submit
// Case 3: One mapping, one or more options - Dialog Steps
// Case 4: Multiple mappings, 0 or more options - Dialog, then based on mapping, either submit or show the flow

interface PushToCRMGroupProps {
  provider: any;
  variant?: 'compact' | 'regular';
  sx?: any;
  newHeaderView?: boolean;
  entityType?: string;
  testIdPrefix?: string;
}

const PushToCRMGroup = ({
  provider,
  variant = 'regular',
  entityType,
  testIdPrefix = '',
}: PushToCRMGroupProps) => {
  const { type } = useParams<{ type: string }>();
  const pathname = usePathname();
  const log = useLogging();

  const { clickedButtons, setClickedButtons, reenableButton } =
    useCRMButtonContext();
  const isDisabled = clickedButtons.includes(provider?.id);

  const normalizedType = type?.toLowerCase();
  const contentType = entityType
    ? entityType.toLowerCase()
    : normalizedType && entityTypeSlugMap[normalizedType]
    ? entityTypeSlugMap[normalizedType]
    : provider?.type?.toLowerCase() || provider?.content_type?.toLowerCase();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const { setNotification } = useNotification();
  const { setPushDuplicateToCRM, setPushProviderToCRM } = useLayoutControl();

  const [pushToCRM, { isLoading }] = usePushToCRMMutation();

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  //find any enabled mappings
  const enabledCrmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  //find any disabled mappings
  const disabledCrmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => !mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  const mappings = useMemo(() => {
    return allMappings?.results?.filter(
      ({ medscout_object_type, enabled }) =>
        medscout_object_type?.toLowerCase() === contentType && enabled
    );
  }, [allMappings, contentType]);

  const isThirdParty = enabledCrmTypes[0]?.toLowerCase() === 'third_party';

  const singleMappingId = mappings?.length === 1 ? mappings[0].id : null;
  const { data: options, isLoading: optionsLoading } =
    useGetUserInputOptionsQuery(
      {
        mappingId: singleMappingId,
        page: 0,
        pageSize: 100,
      },
      { skip: !singleMappingId }
    );

  const pushToCrmHandler = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    // Log click event - check if we're in StrategyCenter context
    const isStrategyContext = pathname.includes('strategy-center');
    if (isStrategyContext) {
      log.event('Strategy push to CRM clicked', {
        source: 'StrategyResultItem',
        providerId: provider?.provider_id,
        providerName: provider?.name,
        providerType: contentType,
        hasExistingLinks: provider?.crm_links?.length > 0,
        mappingsCount: mappings?.length || 0,
      });
    }

    //Check if disabled
    if (isDisabled) return;

    // Check if mappings or provider_id is missing
    if (!mappings?.length || !provider.provider_id) return;

    // Determine if all options are hidden
    const isAllHidden = options?.results?.every(
      (option) => option?.hidden === true
    );

    // Case: Only one mapping and all options are hidden
    if (mappings.length === 1 && (!options?.results?.length || isAllHidden)) {
      // There could be options, but they are all hidden
      const fieldsToPush = options?.results?.reduce((acc, option) => {
        acc[option?.crm_api_code] = option?.extra?.default_value || '';
        return acc;
      }, {});

      try {
        const response = await pushToCRM({
          object_type: mappings[0]?.medscout_object_type,
          mapping: mappings[0]?.id,
          fields: fieldsToPush,
          object_id: provider?.provider_id,
        }).unwrap();

        if (response.duplicate) {
          const duplicateData = {
            mapping: mappings[0]?.id,
            object_type: mappings[0]?.medscout_object_type,
            object_id: provider?.provider_id,
            ...response,
          };
          setPushDuplicateToCRM(duplicateData);
          return;
        }

        setNotification({
          title: isThirdParty
            ? 'Push to CRM Initiated'
            : 'Push to CRM successful',
          message: isThirdParty
            ? 'Successfully initiated push to CRM. This may take a few minutes.'
            : 'Successfully pushed to CRM.',
          type: 'success',
        });
        if (isThirdParty) {
          setClickedButtons(provider?.id);
        }
        log.event('Push to CRM', {
          ...provider,
        });
      } catch (err: any) {
        setNotification({
          title: 'Push to CRM failed',
          message:
            err?.error_message ||
            'Error pushing to CRM, please try again later.',
          type: 'error',
        });
        log.exception(`Error pushing to CRM: ${err}`);
      }
    } else {
      // Case: Multiple mappings or visible options available

      const normalizedProvider = {
        ...provider,
        content_type: provider.content_type || provider.type || contentType,
      };

      setPushProviderToCRM(normalizedProvider, enabledCrmTypes[0]);
    }
  };

  const toggleMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // Toggle: if already open, close it; otherwise open it
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(e.currentTarget);
    }
  };

  const { count, label, crmLinks } = useMemo(() => {
    const crmLinks =
      provider?.crm_links?.filter((link) => {
        const linkCrm = link.crm?.toLowerCase();
        // Show links from any CRM type that has a mapping (enabled or disabled)
        return (
          enabledCrmTypes.some((t) => t === linkCrm) ||
          disabledCrmTypes.some((t) => t === linkCrm)
        );
      }) || [];

    const count = crmLinks.length;
    const firstCrmLink = crmLinks[0] || {};
    const firstLinkStatus = firstCrmLink.crm_status;
    const label =
      count === 1 && firstLinkStatus
        ? firstLinkStatus
        : count == 1
        ? `${firstCrmLink?.owner?.firstname || ''} ${
            firstCrmLink?.owner?.lastname || ''
          }`.trim() || `View in CRM`
        : count > 1
        ? `${count} Records`
        : 'Push to CRM';

    return { count, label, crmLinks };
  }, [disabledCrmTypes, enabledCrmTypes, provider]);

  useEffect(() => {
    if (count) reenableButton(provider?.id);
  }, [count, provider?.id, reenableButton]);

  const isMedRM =
    enabledCrmTypes[0]?.toLowerCase() === 'medrm' ||
    disabledCrmTypes[0]?.toLowerCase() === 'medrm';

  const pushToCRMEnabled = useMemo(() => {
    return (
      allMappings?.results?.filter(
        ({ medscout_object_type, enabled }) =>
          medscout_object_type?.toLowerCase() === contentType && enabled
      ).length > 0
    );
  }, [allMappings?.results, contentType]);

  const hasLinks = provider?.crm_links?.length > 0;

  const integration = enabledCrmTypes[0] || disabledCrmTypes[0];

  // if no provider OR no integration mappings set up OR mapping is not enabled/provider has no links OR is med rm, do not render button
  if (
    !provider ||
    allMappings?.count < 1 ||
    (!pushToCRMEnabled && !hasLinks) ||
    isMedRM
  )
    return null;

  // Legacy MUI components
  return (
    <div className="flex items-center max-w-full overflow-hidden">
      <PushToCRMV2
        id={`discovery-push-to-crm-button-${provider?.provider_id}`}
        label={label}
        hasLinks={!!count}
        isLoading={isLoading || optionsLoading}
        onSubmit={!!count ? toggleMenu : pushToCrmHandler}
        variant={variant}
        disabled={isDisabled}
        testIdPrefix={testIdPrefix}
        isOpen={!!anchorEl}
        integration={integration}
      />
      <CRMMaxMenuV2
        integration={integration}
        options={crmLinks || []}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        provider={provider}
      />
    </div>
  );
};

export default PushToCRMGroup;
